import { lazy, useEffect, Suspense } from "react"

import { Box, Spinner } from "@chakra-ui/react"
import {
  Route,
  Routes,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom"

import PermittedContentOnly from "components/PermittedContentOnly"
import ProtectedComponent from "components/ProtectedComponents"
import { useAppSelector } from "hooks/store/hooks"
import SystemEmailLogPage from "pages/SystemEmailLogPage"
import SystemEmailLogDetails from "pages/SystemEmailLogPage/SystemEmailLog"
import { selectIsUserLoggedIn } from "stores/auth/authSlice"
import { Endpoints } from "types"

const Index = lazy(() => import("pages/Index"))
const UserDashboardPage = lazy(() => import("pages/UserDashboardPage"))
const EmailCommunicationPage = lazy(
  () => import("pages/EmailCommunicationPage"),
)
const EmailCommunicationDetails = lazy(
  () => import("pages/EmailCommunicationPage/EmailCommunicationDetails"),
)
const ForwardEmail = lazy(
  () => import("pages/EmailCommunicationPage/ForwardEmail"),
)
const ProxyRulesIndex = lazy(
  () => import("pages/ProxyCommunication/ProxyRulesIndex"),
)
const NewBookingDashboardPage = lazy(() => import("pages/NewBookingPage"))
const BcomBookingDashboard = lazy(() => import("pages/BcomBookingDashboard"))
const BcomBookingDetails = lazy(
  () => import("pages/BcomBookingDashboard/BcomBookingDetails"),
)
const BookingDetailV3 = lazy(() => import("pages/BookingDetailV3"))
const ExtensionOfferManageDashboard = lazy(
  () => import("pages/ExtensionOfferManageDashboard"),
)
const HCFChargeDashboard = lazy(() => import("pages/HCFChargeDashboard"))
const CompanyDashboard = lazy(() => import("pages/CompanyDashboard"))
const DealsDashboardPage = lazy(() => import("pages/DealsDashboardPage/index"))
const ButtonPage = lazy(() => import("pages/comps/ButtonPage"))
const BookingPrimitivesPage = lazy(
  () => import("pages/comps/BookingPrimitivesPage"),
)
const DropDownPage = lazy(() => import("pages/comps/DropDownPage"))
const InputPage = lazy(() => import("pages/comps/InputPage"))
const SearchableDropDownPage = lazy(
  () => import("pages/comps/SearchableDropDownPage"),
)
const TablePage = lazy(() => import("pages/comps/Table/TablePage"))
const TextPage = lazy(() => import("pages/comps/TextPage"))
const SendEmailsPage = lazy(() => import("pages/SendEmails"))
const ReviewManagementPage = lazy(() => import("pages/ReviewManagement"))
const FeeMappingPage = lazy(() => import("pages/FeeMapping"))
const HeroBannerPage = lazy(() => import("pages/HeroBannerPage"))
const PropertyManagementPage = lazy(
  () => import("pages/PropertyManagementPage"),
)
const EventsPage = lazy(() => import("pages/EventsPage"))
const FaqPage = lazy(() => import("pages/FaqPage"))
const HostPayoutDashboardPage = lazy(() => import("pages/HostPayoutPage"))
const ManageTokenPage = lazy(() => import("pages/ManageTokenPage"))
const PermissionsPage = lazy(() => import("pages/PermissionsPage"))
const ManageUsersPage = lazy(() => import("pages/ManageUsersPage"))
const RegionalLandingPage = lazy(() => import("pages/RegionalLandingPage"))
const OnboardingPage = lazy(() => import("pages/Onboarding/OnboardingPage"))
const PMSFeesManageTokenPage = lazy(() => import("pages/PMSFeesManagePage"))
const ManagePromoCodesPage = lazy(() => import("pages/ManagePromoCodePage"))
const RedeemPromocodePage = lazy(
  () => import("pages/ManagePromoCodePage/RedeemPromocodePage"),
)
const PropertyDashboardPage = lazy(
  () => import("pages/PropertyDashboard/PropertyDashboardPage"),
)
const CallerVerificationPage = lazy(
  () => import("pages/CallerVerificationPage"),
)
const ReportsPage = lazy(() => import("pages/ReportsPage"))
const PropertyDiscount = lazy(
  () => import("pages/WimstayDiscount/PropertyDiscount"),
)
const CompanyDiscount = lazy(
  () => import("pages/WimstayDiscount/CompanyDiscount"),
)
const TravelAgentDashboardPage = lazy(
  () => import("pages/TravelAgentDashboard"),
)
const AgentManagementPage = lazy(() => import("pages/AgentManagement"))
const CommissionDashboardPage = lazy(() => import("pages/CommissionDashboard"))
const CjCommissionDashboardPage = lazy(
  () => import("pages/CjCommissionDashboard"),
)
const AgentPayoutDashboardPage = lazy(
  () => import("pages/AgentPayoutDashboard"),
)
const AdminContactInfoPage = lazy(
  () => import("pages/AdminContactInfoPage/index"),
)
const CheckInsPage = lazy(() => import("pages/CheckInsPage"))

/** A retired dashboard's booking link, opened on the v3 detail page. */
const RetiredBookingDetails = () => {
  const { id = "" } = useParams<{ id: string }>()
  return <Navigate to={`/bookings/v3/${encodeURIComponent(id)}`} replace />
}

const Router = () => {
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/", { replace: true })
    }
  }, [isLoggedIn, navigate])

  return (
    <Suspense
      fallback={
        <Box textAlign={"center"} marginY={"100px"}>
          <Spinner />
        </Box>
      }
    >
      <Routes>
        <Route
          path={Endpoints.index}
          element={
            !isLoggedIn ? (
              <Index />
            ) : (
              <Navigate to={Endpoints.propertyDashboard} replace />
            )
          }
        />
        <Route
          path={Endpoints.propertyDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='PropertyDashboard'>
                <PropertyDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.callerVerification}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='CallerVerification'>
                <CallerVerificationPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.userDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='UserDashboard'>
                <UserDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.emailCommunication}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <EmailCommunicationPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.emailCommunicationDetails}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <EmailCommunicationDetails />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.systemEmailLogDetails}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <SystemEmailLogDetails />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.forwardEmail}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <ForwardEmail />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.proxyRules}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <ProxyRulesIndex />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        {/*
          The old booking dashboards are retired: v3 replaces them. Their URLs
          redirect so bookmarks and shared links land on the same booking.
        */}
        <Route
          path={Endpoints.bookingDashboard}
          element={<Navigate to={Endpoints.bookingDashboardV3} replace />}
        />
        <Route
          path={Endpoints.bookingDashboardDetails}
          element={<RetiredBookingDetails />}
        />
        <Route
          path={Endpoints.bookingInvoice}
          element={<Navigate to={Endpoints.bookingDashboardV3} replace />}
        />
        <Route
          path={Endpoints.newBookingDashboard}
          element={<Navigate to={Endpoints.bookingDashboardV3} replace />}
        />
        <Route
          path={Endpoints.newBookingDashboardDetails}
          element={<RetiredBookingDetails />}
        />

        {/*
          The v3 booking dashboard reuses the old dashboard's list page; its
          rows open the v3 detail page.
        */}
        <Route
          path={Endpoints.bookingDashboardV3}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <NewBookingDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.bcomBookingDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <BcomBookingDashboard />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.bcomBookingDashboardDetails}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <BcomBookingDetails />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.bookingDetailV3}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <BookingDetailV3 />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.extensionOfferManage}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <ExtensionOfferManageDashboard />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.hcfChargeDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <HCFChargeDashboard />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.companyDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='CompanyDashboard'>
                <CompanyDashboard />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.dealsDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='DealsDashboard'>
                <DealsDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.manageCompanyDiscount}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='WhimstayDiscount'>
                <CompanyDiscount />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.managePropertyDiscount}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='WhimstayDiscount'>
                <PropertyDiscount />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.hostPayoutDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='HostPayoutDashboard'>
                <HostPayoutDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.faq}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='FAQ'>
                <FaqPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.reports}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='Reports'>
                <ReportsPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.onboarding}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='OnboardingDashboard'>
                <OnboardingPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.managePromoCodes}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='PromoCodeDashboard'>
                <ManagePromoCodesPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.redeemPromoCodes}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='PromoCodeDashboard'>
                <RedeemPromocodePage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />

        <Route
          path={Endpoints.systemEmailLog}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='EmailCommunication'>
                <SystemEmailLogPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.manageTokens}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='ManageTokens'>
                <ManageTokenPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.pmsFeesManage}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='PMSFeesManage'>
                <PMSFeesManageTokenPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.events}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='Events'>
                <EventsPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.sendEmails}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='SendEmails'>
                <SendEmailsPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.reviewManagement}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='ReviewManagement'>
                <ReviewManagementPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.feeMapping}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='FeeMapping'>
                <FeeMappingPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.heroBanner}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='HeroConfiguration'>
                <HeroBannerPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.propertyManagement}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='HeroConfiguration'>
                <PropertyManagementPage
                  staticTypeId={1}
                  key='Featured Property'
                />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.newToWhimstay}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='HeroConfiguration'>
                <PropertyManagementPage
                  staticTypeId={3}
                  key='New to Whimstay'
                />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.manageUsers}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='ManageUsers'>
                <ManageUsersPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.regionalLanding}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='RegionalLanding'>
                <RegionalLandingPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.manageRole}
          element={
            <ProtectedComponent>
              {/*<PermittedContentOnly module='ManageUsers'>*/}
              <PermissionsPage />
              {/*</PermittedContentOnly>*/}
            </ProtectedComponent>
          }
        />
        <Route path={"/comps/btn"} element={<ButtonPage />} />
        <Route path={"/comps/booking"} element={<BookingPrimitivesPage />} />
        <Route path={"/comps/table"} element={<TablePage />} />
        <Route path={"/comps/text"} element={<TextPage />} />
        <Route path={"/comps/input"} element={<InputPage />} />
        <Route
          path={"/comps/searchDropdown"}
          element={<SearchableDropDownPage />}
        />
        <Route path={"/comps/dropdown"} element={<DropDownPage />} />
        <Route
          path={Endpoints.travelAgentDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='TravelAgentDashboard'>
                <TravelAgentDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.agentManagement}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='TravelAgentDashboard'>
                <AgentManagementPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.commissionDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='CommissionDashboard'>
                <CommissionDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.cjCommissionDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='CjCommissionDashboard'>
                <CjCommissionDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.agentPayoutDashboard}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='TravelAgentDashboard'>
                <AgentPayoutDashboardPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.hostContactInfo}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <AdminContactInfoPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route
          path={Endpoints.checkIns}
          element={
            <ProtectedComponent>
              <PermittedContentOnly module='BookingDashboard'>
                <CheckInsPage />
              </PermittedContentOnly>
            </ProtectedComponent>
          }
        />
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>
    </Suspense>
  )
}

export default Router
